import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader, Clock3 } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import SEO from "./SEO";
import { useTheme } from "../context/ThemeContext";
import { useNotification } from "../context/NotificationContext";
import "./AdminSubscriptionHistory.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const planLabel = (plan) => {
  if (plan === "essentials") return "Essential";
  if (plan === "professional") return "Professional";
  return "Free";
};

const eventLabel = (eventType) => {
  switch (eventType) {
    case "trial_started":
      return "Trial Started";
    case "trial_ended":
      return "Trial Ended";
    case "subscription_started":
      return "Subscription Started";
    case "plan_changed":
      return "Plan Changed";
    case "cancellation_scheduled":
      return "Cancellation Scheduled";
    case "subscription_canceled":
      return "Subscription Canceled";
    case "renewed":
      return "Subscription Renewed";
    case "payment_failed":
      return "Payment Failed";
    case "payment_recovered":
      return "Payment Recovered";
    case "payment_retry_failed":
      return "Payment Retry Failed";
    default:
      return "Status Updated";
  }
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return "—";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

export default function AdminSubscriptionHistory() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [user, setUser] = useState(null);

  const authHeaders = useMemo(() => {
    const headers = { "Content-Type": "application/json" };
    const token = localStorage.getItem("token");
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, []);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_URL}/auth/admin/users/${encodeURIComponent(userId)}/subscription-history`,
          {
            headers: authHeaders,
            credentials: "include",
          },
        );
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || "Failed to load subscription history");
        }
        const data = await response.json();
        setUser(data.user || null);
        setHistory(Array.isArray(data.history) ? data.history : []);
      } catch (error) {
        showError(error.message || "Failed to load subscription history");
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchHistory();
    }
  }, [authHeaders, showError, userId]);

  return (
    <div className={`admin-sub-history-page ${darkMode ? "dark" : ""}`}>
      <SEO title="Admin Subscription History" description="User subscription history details." />
      <Header />
      <main className="admin-sub-history-content">
        <div className="admin-sub-history-top">
          <button
            type="button"
            className="admin-sub-history-back-btn"
            onClick={() => navigate("/admin")}
          >
            <ArrowLeft size={16} />
            Back to Admin
          </button>
          <h1>Subscription History</h1>
          {user && (
            <p className="admin-sub-history-user">
              {user.name || "Unnamed User"} ({user.email})
            </p>
          )}
        </div>

        {loading ? (
          <div className="admin-sub-history-loading">
            <Loader className="spin" size={24} />
            <span>Loading subscription history...</span>
          </div>
        ) : history.length === 0 ? (
          <div className="admin-sub-history-empty">
            <Clock3 size={20} />
            <span>No subscription history found for this user yet.</span>
          </div>
        ) : (
          <div className="admin-sub-history-list">
            {history.map((entry, index) => {
              const fromPlan = entry.fromPlan ? planLabel(entry.fromPlan) : null;
              const toPlan = entry.toPlan ? planLabel(entry.toPlan) : null;
              const switchText =
                fromPlan && toPlan && fromPlan !== toPlan
                  ? `${fromPlan} -> ${toPlan}`
                  : toPlan || fromPlan || "—";
              const actor =
                entry.cancellationBy ||
                (entry.eventType === "plan_changed" ? "self" : "—");
              const source = entry.cancellationSource || "—";
              const isCancellationEvent =
                entry.eventType === "cancellation_scheduled" ||
                entry.eventType === "subscription_canceled";

              return (
                <article key={`${entry.createdAt || "na"}-${index}`} className="admin-sub-history-card">
                  <div className="admin-sub-history-row">
                    <strong>{eventLabel(entry.eventType)}</strong>
                    <span>{formatDateTime(entry.createdAt)}</span>
                  </div>

                  <ul className="admin-sub-history-bullets">
                    <li>
                      <strong>Plan Movement:</strong> {switchText}
                    </li>
                    <li>
                      <strong>Status:</strong> {(entry.fromStatus || "—")} -> {(entry.toStatus || "—")}
                    </li>
                    <li>
                      <strong>Effective Window:</strong> {formatDateTime(entry.effectiveStart)} - {formatDateTime(entry.effectiveEnd)}
                    </li>
                    <li>
                      <strong>Cancellation:</strong> By: {isCancellationEvent ? actor : ""} | Source: {isCancellationEvent ? source : ""}
                    </li>
                  </ul>
                </article>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
